import React, { useState, useEffect, useCallback } from "react";
import { getEtapes, createEtape, updateEtape } from "../services/api";
import ModaleV2 from "./ModaleV2";
import { dateCourte, formatDate, formatHeures, etapeEnRetard, largeurJauge } from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : etapes
//
// Liste des etapes actives, avec avancement, retard et temps passe.
// Creation et modification : chef, co-chefs et acteur principal
// (droits.peut_gerer_etapes). Pas de suppression : une etape se
// desactive, ses heures et commentaires restent.
//
// Props : projetId, collaborateurId, droits, onModifie (avancement du
// projet recalcule par le backend : la page recharge son en-tete)
// ===================================================================

const ETAPE_VIDE = { nom: "", date_debut: "", date_fin_prevue: "", avancement: 0 };

function ModaleEtape({ etape, onEnregistrer, onDesactiver, onClose }) {
  const [valeurs, setValeurs] = useState(() =>
    etape
      ? {
          nom: etape.nom,
          date_debut: dateCourte(etape.date_debut),
          date_fin_prevue: dateCourte(etape.date_fin_prevue),
          avancement: etape.avancement || 0,
        }
      : ETAPE_VIDE
  );
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const changer = (champ) => (e) => setValeurs((v) => ({ ...v, [champ]: e.target.value }));

  const executer = async (action) => {
    setEnCours(true);
    setErreur(null);
    try {
      await action();
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  const enregistrer = () =>
    executer(() =>
      onEnregistrer({
        nom: valeurs.nom.trim(),
        date_debut: valeurs.date_debut || null,
        date_fin_prevue: valeurs.date_fin_prevue || null,
        avancement: Number(valeurs.avancement) || 0,
      })
    );

  return (
    <ModaleV2
      titre={etape ? "Modifier l'étape" : "Nouvelle étape"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          {etape && (
            <button type="button" className="v2-btn v2-btn-secondaire v2-btn-danger-texte" disabled={enCours}
              onClick={() => executer(onDesactiver)}>
              Désactiver l'étape
            </button>
          )}
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>
            Annuler
          </button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={enregistrer}
            disabled={enCours || !valeurs.nom.trim()}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <div className="v2-champ">
        <label className="v2-label" htmlFor="etape-nom">Nom<span className="v2-requis">*</span></label>
        <input id="etape-nom" value={valeurs.nom} onChange={changer("nom")} />
      </div>
      <div className="v2-champs-ligne">
        <div className="v2-champ">
          <label className="v2-label" htmlFor="etape-debut">Début</label>
          <input id="etape-debut" type="date" value={valeurs.date_debut} onChange={changer("date_debut")} />
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="etape-fin">Fin prévue</label>
          <input id="etape-fin" type="date" value={valeurs.date_fin_prevue} onChange={changer("date_fin_prevue")} />
        </div>
      </div>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="etape-avancement">Avancement : {valeurs.avancement} %</label>
        <input id="etape-avancement" type="range" min="0" max="100" step="5" className="v2-curseur"
          value={valeurs.avancement} onChange={changer("avancement")} />
        <span className="v2-champ-aide">À 100 %, l'étape est terminée et sort de la saisie des temps.</span>
      </div>
    </ModaleV2>
  );
}

function ProjetEtapesV2({ projetId, collaborateurId, droits, onModifie }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [modale, setModale] = useState(null); // null | { etape: null (creation) | objet }

  const charger = useCallback(() => {
    setErreur(null);
    return getEtapes(projetId, collaborateurId)
      .then(setDonnees)
      .catch((err) => setErreur(`Étapes non chargées : ${err.message}`));
  }, [projetId, collaborateurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  const apresEcriture = async () => {
    setModale(null);
    await charger();
    if (onModifie) onModifie();
  };

  const enregistrer = async (valeurs) => {
    if (modale.etape) await updateEtape(modale.etape.id, valeurs, collaborateurId);
    else await createEtape(projetId, valeurs, collaborateurId);
    await apresEcriture();
  };

  const desactiver = async () => {
    await updateEtape(modale.etape.id, { active: false }, collaborateurId);
    await apresEcriture();
  };

  const etapes = donnees?.etapes || [];
  const totalHeures = etapes.reduce((s, e) => s + (Number(e.heures_passees) || 0), 0);
  const peutGerer = Boolean(droits?.peut_gerer_etapes);

  return (
    <section className="v2-panneau v2-projet-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">
          Étapes
          {donnees && <span className="v2-projet-compte">{donnees.etapes_terminees} / {donnees.total_etapes} terminées</span>}
        </h2>
        {peutGerer && (
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => setModale({ etape: null })}>
            Nouvelle étape
          </button>
        )}
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {!donnees && !erreur && <div className="v2-chargement">Chargement…</div>}

      {donnees && (
        <div className="v2-tableau-conteneur">
          <table className="v2-tableau v2-projet-etapes">
            <thead>
              <tr>
                <th scope="col">Étape</th>
                <th scope="col">Début</th>
                <th scope="col">Fin prévue</th>
                <th scope="col" className="v2-projet-col-avancement">Avancement</th>
                <th scope="col" className="v2-num">Temps passé</th>
                {peutGerer && <th scope="col" className="v2-projet-col-actions"><span className="v2-invisible">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {etapes.length === 0 && (
                <tr><td colSpan={peutGerer ? 6 : 5} className="v2-vide">Aucune étape pour l'instant.</td></tr>
              )}
              {etapes.map((e) => (
                <tr key={e.id}>
                  <td className="v2-gras">
                    {e.nom}
                    {etapeEnRetard(e) && <> <span className="v2-statut v2-statut-danger">En retard</span></>}
                    {(e.avancement || 0) >= 100 && <> <span className="v2-statut v2-statut-success">Terminée</span></>}
                  </td>
                  <td className="v2-cellule-discrete">{formatDate(e.date_debut)}</td>
                  <td className="v2-cellule-discrete">{formatDate(e.date_fin_prevue)}</td>
                  <td>
                    <div className="v2-projet-jauge">
                      <span className="v2-barres-piste">
                        <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(e.avancement) }} />
                      </span>
                      <span className="v2-projet-jauge-valeur">{e.avancement || 0} %</span>
                    </div>
                  </td>
                  <td className="v2-num">{formatHeures(e.heures_passees)}</td>
                  {peutGerer && (
                    <td>
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => setModale({ etape: e })}>
                        Modifier
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {etapes.length > 0 && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={4}>Temps passé sur les étapes</th>
                  <td className="v2-num">{formatHeures(totalHeures)}</td>
                  {peutGerer && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {donnees && Number(donnees.heures_avant_refonte) > 0 && (
        <p className="v2-champ-aide v2-projet-note">
          Temps projet avant la refonte (non réparti par étape) : {formatHeures(donnees.heures_avant_refonte)}.
        </p>
      )}

      {modale && (
        <ModaleEtape
          etape={modale.etape}
          onEnregistrer={enregistrer}
          onDesactiver={desactiver}
          onClose={() => setModale(null)}
        />
      )}
    </section>
  );
}

export default ProjetEtapesV2;
